import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Prohibit, Trash, CalendarX, Plus, Clock } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DURATIONS = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'first_half', label: 'First Half (12 AM – 12 PM)' },
  { value: 'second_half', label: 'Second Half (12 PM – 12 AM)' },
  { value: 'custom', label: 'Custom time range' },
];

const REASONS = [
  { value: 'maintenance', label: 'Closed for Maintenance' },
  { value: 'lunch_break', label: 'Lunch Break' },
  { value: 'out_of_service', label: 'Out of Service' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'personal', label: 'Personal / Unavailable' },
  { value: 'other', label: 'Temporarily Closed' },
];

const durationBadge = (c) => {
  if (c.duration_type === 'full_day') return 'Full Day';
  if (c.duration_type === 'first_half') return 'First Half';
  if (c.duration_type === 'second_half') return 'Second Half';
  return `${c.start_time} – ${c.end_time}`;
};

export const ClosurePanel = ({ listingId = null, onChanged = null }) => {
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(listingId || '');
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    date: today, end_date: '', duration_type: 'full_day',
    start_time: '12:00', end_time: '14:00', reason: 'maintenance', note: '',
  });

  const fetchClosures = useCallback(async () => {
    try {
      const params = listingId ? `?listing_id=${listingId}` : '';
      const res = await axios.get(`${API_URL}/api/seller/closures${params}`);
      setClosures(res.data.closures || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [listingId]);

  useEffect(() => {
    fetchClosures();
    if (!listingId) {
      axios.get(`${API_URL}/api/listings/my-listings`)
        .then(res => {
          const ls = res.data.listings || [];
          setListings(ls);
          if (ls[0] && !selectedListing) setSelectedListing(ls[0].listing_id);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchClosures, listingId]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const handleAdd = async () => {
    const target = listingId || selectedListing;
    if (!target) { flash('Pick a listing first'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/seller/closures`, {
        listing_id: target,
        date: form.date,
        end_date: form.end_date || form.date,
        duration_type: form.duration_type,
        start_time: form.duration_type === 'custom' ? form.start_time : null,
        end_time: form.duration_type === 'custom' ? form.end_time : null,
        reason: form.reason,
        note: form.note || null,
      });
      flash('Space closed for the selected period');
      setForm(f => ({ ...f, end_date: '', note: '' }));
      await fetchClosures();
      onChanged && onChanged();
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed to add closure');
    } finally { setBusy(false); }
  };

  const handleDelete = async (closureId) => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/seller/closures/${closureId}`);
      setClosures(prev => prev.filter(c => c.closure_id !== closureId));
      flash('Closure removed — space reopened');
      onChanged && onChanged();
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const visible = listingId ? closures : closures.filter(c => !selectedListing || c.listing_id === selectedListing);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="closure-panel">
      <div className="px-4 py-3 bg-gradient-to-r from-rose-50 to-orange-50 border-b border-rose-100 flex items-center gap-2">
        <Prohibit size={18} weight="bold" className="text-rose-500" />
        <h3 className="text-sm font-bold text-slate-900">Space Closures</h3>
        <span className="text-[11px] text-slate-500">Close your space or slots for a duration — buyers will see this</span>
      </div>

      <div className="p-4 space-y-3">
        {msg && (
          <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-xs" data-testid="closure-msg">{msg}</div>
        )}

        {/* Add form */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {!listingId && (
            <div className="col-span-2 sm:col-span-3">
              <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Listing</label>
              <select value={selectedListing} onChange={(e) => setSelectedListing(e.target.value)}
                className="w-full h-9 px-2 border border-slate-200 rounded-lg text-xs bg-white" data-testid="closure-listing-select">
                {listings.map(l => <option key={l.listing_id} value={l.listing_id}>{l.title}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">From date *</label>
            <Input type="date" min={today} value={form.date}
              onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              className="h-9 rounded-lg text-xs" data-testid="closure-date" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">To date (optional)</label>
            <Input type="date" min={form.date} value={form.end_date}
              onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
              className="h-9 rounded-lg text-xs" data-testid="closure-end-date" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Duration</label>
            <select value={form.duration_type} onChange={(e) => setForm(f => ({ ...f, duration_type: e.target.value }))}
              className="w-full h-9 px-2 border border-slate-200 rounded-lg text-xs bg-white" data-testid="closure-duration">
              {DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          {form.duration_type === 'custom' && (
            <>
              <div>
                <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Start time</label>
                <Input type="time" value={form.start_time}
                  onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
                  className="h-9 rounded-lg text-xs" data-testid="closure-start-time" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">End time</label>
                <Input type="time" value={form.end_time}
                  onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
                  className="h-9 rounded-lg text-xs" data-testid="closure-end-time" />
              </div>
            </>
          )}
          <div>
            <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Reason *</label>
            <select value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
              className="w-full h-9 px-2 border border-slate-200 rounded-lg text-xs bg-white" data-testid="closure-reason">
              {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[11px] font-medium text-slate-500 mb-0.5 block">Note (optional)</label>
            <Input value={form.note} maxLength={200}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="e.g. annual servicing" className="h-9 rounded-lg text-xs" data-testid="closure-note" />
          </div>
        </div>
        <Button onClick={handleAdd} disabled={busy || !form.date}
          className="bg-rose-500 hover:bg-rose-600 rounded-xl h-9 text-xs" data-testid="closure-add-btn">
          <Plus size={14} className="mr-1" weight="bold" /> Close Space
        </Button>

        {/* Upcoming closures */}
        <div className="pt-2 border-t border-slate-100">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Upcoming closures</p>
          {loading ? (
            <p className="text-xs text-slate-400">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="no-closures">
              <CalendarX size={14} /> No upcoming closures — space fully open
            </p>
          ) : (
            <div className="space-y-1.5" data-testid="closure-list">
              {visible.map(c => (
                <div key={c.closure_id} className="flex items-center justify-between gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-100" data-testid={`closure-${c.closure_id}`}>
                  <div className="min-w-0 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-800">{c.date}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold flex items-center gap-1">
                      <Clock size={10} /> {durationBadge(c)}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{c.reason_label}</span>
                    {!listingId && <span className="text-[10px] text-slate-400 truncate max-w-[160px]">{c.listing_title}</span>}
                    {c.note && <span className="text-[10px] text-slate-400 italic truncate max-w-[160px]">“{c.note}”</span>}
                  </div>
                  <button onClick={() => handleDelete(c.closure_id)} disabled={busy}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                    title="Reopen (remove closure)" data-testid={`closure-delete-${c.closure_id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
